/**
 * real-google change: Google Cloud OAuth client administration (Admin -> System operations ->
 * Integrations -> Google Cloud) and the desktop's client fetch (spec section 3 table, section 14).
 *
 * The server holds the OAuth client ids in configuration and the client secrets in the SecretStore.
 * No admin route in this file ever carries a secret value in a response: `*SecretSet` booleans are
 * the only admin-visible signal and the config response schema is strict so an accidental
 * `desktopClientSecret` key fails validation server-side. The desktop fetches the desktop client
 * (id + secret) over `integrations.google.client` at connect time and holds it in main-process
 * memory only; that route is `noBodyLog`.
 */
import { z } from 'zod';
import { UserIdSchema } from '../ids.js';
import { EmptyRequestSchema, IsoDateTimeSchema, NoQuerySchema, defineRoute } from './common.js';

// ---------------------------------------------------------------------------------------------
// Google endpoints and the fixed scope set
// ---------------------------------------------------------------------------------------------

/** OpenID issuer used when Google Workspace is the workforce identity provider. */
export const GOOGLE_ISSUER = 'https://accounts.google.com';
export const GOOGLE_DISCOVERY_URL = 'https://accounts.google.com/.well-known/openid-configuration';

/** OAuth 2.0 endpoints the desktop uses for the Google connect flow (native app, PKCE, loopback). */
export const GOOGLE_OAUTH_ENDPOINTS = {
  authorizeUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
  tokenUrl: 'https://oauth2.googleapis.com/token',
  revokeUrl: 'https://oauth2.googleapis.com/revoke',
  userinfoUrl: 'https://openidconnect.googleapis.com/v1/userinfo',
} as const;

/**
 * The fixed scope set. Shown read-only in the admin console, returned by the server on the desktop
 * client route and requested exactly as-is by the desktop (nothing widens it, SEC-06).
 */
export const GOOGLE_INTEGRATION_SCOPES = [
  'openid',
  'email',
  'profile',
  'https://www.googleapis.com/auth/drive.file',
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/gmail.compose',
] as const;
export type GoogleIntegrationScope = (typeof GOOGLE_INTEGRATION_SCOPES)[number];

/** Terse purpose line per scope for the read-only list and the setup docs. */
export const GOOGLE_INTEGRATION_SCOPE_PURPOSE: Record<GoogleIntegrationScope, string> = {
  openid: 'Identity of the connected account',
  email: 'Connected account email',
  profile: 'Connected account display name',
  'https://www.googleapis.com/auth/drive.file': 'Files this app creates or the user selects',
  'https://www.googleapis.com/auth/documents': 'Agenda documents',
  'https://www.googleapis.com/auth/spreadsheets': 'Tracker spreadsheets',
  'https://www.googleapis.com/auth/gmail.compose': 'Outreach drafts',
};

// ---------------------------------------------------------------------------------------------
// Field schemas
// ---------------------------------------------------------------------------------------------

/**
 * Google OAuth client ids look like `<digits>-<hash>.apps.googleusercontent.com`. The request
 * schema is deliberately looser (no whitespace) so a paste is accepted and stored; the Test action
 * and the admin form use `isGoogleOAuthClientIdFormat` to report a malformed id.
 */
export const GoogleOAuthClientIdSchema = z.string().trim().min(1).max(256).regex(/^\S+$/, 'client id must not contain whitespace');
export const GOOGLE_OAUTH_CLIENT_ID_PATTERN = /^[0-9]+-[a-z0-9]+\.apps\.googleusercontent\.com$/;
export function isGoogleOAuthClientIdFormat(clientId: string): boolean {
  return GOOGLE_OAUTH_CLIENT_ID_PATTERN.test(clientId.trim());
}

/** Write-only secret value (Desktop-app and Web-application clients). Never appears in a response schema. */
export const GoogleOAuthClientSecretSchema = z.string().min(8).max(512);

/** Workspace hosted domain (`hd` claim), e.g. `getagency.com`. Lowercased; no scheme, path or port. */
export const HostedDomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(253)
  .regex(/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'expected a domain name such as example.com');

export const GOOGLE_INTEGRATION_STATUSES = ['configured', 'not_configured'] as const;
export const GoogleIntegrationStatusSchema = z.enum(GOOGLE_INTEGRATION_STATUSES);
export type GoogleIntegrationStatus = z.infer<typeof GoogleIntegrationStatusSchema>;

export const GOOGLE_INTEGRATION_TEST_RESULTS = ['ok', 'failed'] as const;
export const GoogleIntegrationTestResultSchema = z.enum(GOOGLE_INTEGRATION_TEST_RESULTS);
export type GoogleIntegrationTestResult = z.infer<typeof GoogleIntegrationTestResultSchema>;

/** Configured means the desktop client id and its secret are both stored. */
export function deriveGoogleIntegrationStatus(input: { desktopClientId: string | null; desktopClientSecretSet: boolean }): GoogleIntegrationStatus {
  return input.desktopClientId !== null && input.desktopClientSecretSet ? 'configured' : 'not_configured';
}

// ---------------------------------------------------------------------------------------------
// Admin: configuration (masked), update (secrets write-only), test
// ---------------------------------------------------------------------------------------------

export const GoogleIntegrationConfigSchema = z
  .object({
    desktopClientId: GoogleOAuthClientIdSchema.nullable(),
    /** True when a desktop client secret is stored. The value is never returned. */
    desktopClientSecretSet: z.boolean(),
    /** Web-application client, only needed for Google as the workforce sign-in. */
    webClientId: GoogleOAuthClientIdSchema.nullable(),
    webClientSecretSet: z.boolean(),
    hostedDomain: HostedDomainSchema.nullable(),
    /** Always GOOGLE_INTEGRATION_SCOPES; carried so the console renders what the server enforces. */
    scopes: z.array(z.string().min(1)),
    /** "Use Google Workspace for sign-in": requires the web client (id + secret) and the hosted domain. */
    workforceSignIn: z.boolean(),
    status: GoogleIntegrationStatusSchema,
    lastTestedAt: IsoDateTimeSchema.nullable(),
    lastTestResult: GoogleIntegrationTestResultSchema.nullable(),
    /** Null until an administrator saves the integration for the first time. */
    updatedAt: IsoDateTimeSchema.nullable(),
    updatedBy: UserIdSchema.nullable(),
  })
  .strict()
  .superRefine((cfg, ctx) => {
    if (cfg.status !== deriveGoogleIntegrationStatus(cfg)) {
      ctx.addIssue({ code: 'custom', path: ['status'], message: 'status must reflect the desktop client id and secret' });
    }
    if (cfg.workforceSignIn && (cfg.webClientId === null || !cfg.webClientSecretSet || cfg.hostedDomain === null)) {
      ctx.addIssue({ code: 'custom', path: ['workforceSignIn'], message: 'workforce sign-in requires the web client, its secret and the hosted domain' });
    }
  });
export type GoogleIntegrationConfig = z.infer<typeof GoogleIntegrationConfigSchema>;

/**
 * Partial update. Omitted keys keep their value; `null` clears an id or the hosted domain (clearing
 * a client id also drops its stored secret). Secrets are write-only strings. Body logging is off.
 */
export const GoogleIntegrationUpdateRequestSchema = z
  .object({
    desktopClientId: GoogleOAuthClientIdSchema.nullable().optional(),
    desktopClientSecret: GoogleOAuthClientSecretSchema.optional(),
    webClientId: GoogleOAuthClientIdSchema.nullable().optional(),
    webClientSecret: GoogleOAuthClientSecretSchema.optional(),
    hostedDomain: HostedDomainSchema.nullable().optional(),
    workforceSignIn: z.boolean().optional(),
  })
  .strict()
  .refine((body) => Object.values(body).some((v) => v !== undefined), { message: 'at least one field is required' });
export type GoogleIntegrationUpdateRequest = z.infer<typeof GoogleIntegrationUpdateRequestSchema>;

/** Stable failure codes for the Test action; the admin console maps each to copy. */
export const GOOGLE_INTEGRATION_TEST_ERRORS = [
  /** No desktop client id and secret stored yet. */
  'not_configured',
  /** The desktop (or web) client id does not match Google's client id format. */
  'client_id_format',
  /** Google's OpenID discovery document could not be fetched. */
  'discovery_unreachable',
  /** The discovery document was fetched but did not parse or lacks the expected endpoints. */
  'discovery_invalid',
  /** The discovery document's issuer differs from GOOGLE_ISSUER. */
  'issuer_mismatch',
  'unexpected',
] as const;
export const GoogleIntegrationTestErrorSchema = z.enum(GOOGLE_INTEGRATION_TEST_ERRORS);
export type GoogleIntegrationTestError = z.infer<typeof GoogleIntegrationTestErrorSchema>;

export const GoogleIntegrationTestResponseSchema = z
  .object({
    ok: z.boolean(),
    /** Issuer read from the discovery document (present when ok). */
    discoveryIssuer: z.string().url().optional(),
    error: GoogleIntegrationTestErrorSchema.optional(),
    testedAt: IsoDateTimeSchema,
  })
  .strict()
  .refine((r) => (r.ok ? r.discoveryIssuer !== undefined && r.error === undefined : r.error !== undefined), {
    message: 'ok results carry discoveryIssuer; failed results carry error',
  });
export type GoogleIntegrationTestResponse = z.infer<typeof GoogleIntegrationTestResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Desktop: client fetch
// ---------------------------------------------------------------------------------------------

/**
 * The desktop OAuth client. Google documents that a Desktop-app client secret is not confidential;
 * PKCE still applies. The desktop keeps this in main-process memory for the duration of the connect
 * flow and never persists it; the server disables response-body logging on the route.
 */
export const GoogleDesktopClientResponseSchema = z
  .object({
    clientId: GoogleOAuthClientIdSchema,
    clientSecret: z.string().min(1),
    scopes: z.array(z.string().min(1)).min(1),
    hostedDomain: HostedDomainSchema.nullable(),
    authorizeUrl: z.string().url(),
    tokenUrl: z.string().url(),
    revokeUrl: z.string().url(),
    userinfoUrl: z.string().url(),
  })
  .strict();
export type GoogleDesktopClientResponse = z.infer<typeof GoogleDesktopClientResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------------------------

export const integrationRoutes = {
  'admin.integrations.google.get': defineRoute({
    id: 'admin.integrations.google.get',
    method: 'GET',
    path: '/admin/v1/integrations/google',
    auth: 'admin',
    capability: 'device.manage',
    requestSchema: NoQuerySchema,
    responseSchema: GoogleIntegrationConfigSchema,
    description: 'Google Cloud OAuth client configuration with masked secrets (set/not set), status and last test.',
  }),
  'admin.integrations.google.update': defineRoute({
    id: 'admin.integrations.google.update',
    method: 'PUT',
    path: '/admin/v1/integrations/google',
    auth: 'admin',
    capability: 'device.manage',
    recentAuth: true,
    requestSchema: GoogleIntegrationUpdateRequestSchema,
    responseSchema: GoogleIntegrationConfigSchema,
    noBodyLog: true,
    description: 'Update client ids, write-only secrets (to the secret store), hosted domain and workforce sign-in; emits integration.google.updated. Returns the masked configuration.',
  }),
  'admin.integrations.google.test': defineRoute({
    id: 'admin.integrations.google.test',
    method: 'POST',
    path: '/admin/v1/integrations/google/test',
    auth: 'admin',
    capability: 'device.manage',
    requestSchema: EmptyRequestSchema,
    responseSchema: GoogleIntegrationTestResponseSchema,
    description: 'Fetch Google OpenID discovery and validate the client id format; records lastTestedAt/lastTestResult and emits integration.google.tested.',
  }),
  /**
   * Desktop session only. The response carries the desktop client secret, so response-body logging
   * is disabled server-side (`noBodyLog`) and the desktop holds the body in main-process memory only.
   * Answers `integration_not_configured` until an administrator has stored the desktop client.
   */
  'integrations.google.client': defineRoute({
    id: 'integrations.google.client',
    method: 'GET',
    path: '/v1/integrations/google/client',
    auth: 'desktop',
    requestSchema: NoQuerySchema,
    responseSchema: GoogleDesktopClientResponseSchema,
    noBodyLog: true,
    description: 'Desktop OAuth client (id, secret, fixed scopes, hosted domain, Google endpoints) for the connect flow; response-body logging disabled.',
  }),
} as const;
